<script setup>
import { ref, onMounted } from 'vue';
import NinaChat from './NinaChat.vue';
import ReportView from '../../addInsights/report.vue';
import CloserBudgetExceeded from './CloserBudgetExceeded.vue';

const props = defineProps({
    leadId: {
        type: Number,
        required: true,
    },
    leadName: {
        type: String,
        default: '',
    },
    leadCompany: {
        type: String,
        default: '',
    },
    domain: {
        type: [String, null],
        default: null,
    },
});

const emit = defineEmits(['close']);

const activeTab = ref('report');
const insightsReport = ref(null);
const insightsLoading = ref(false);
const insightsAvailable = ref(false);
const insightsError = ref(false);
const budgetExceeded = ref(false);
const retries = ref(0);

const loadInsightsReport = async () => {
    if (!props.domain) {
        insightsError.value = true;
        return;
    }

    insightsLoading.value = true;
    insightsError.value = false;

    try {
        const res = await axios.get(route('nina.insightsReport', props.leadId), {
            params: { domain: props.domain },
        });
        budgetExceeded.value = res.data.status === 'budget_exceeded';
        insightsAvailable.value = res.data.available;
        insightsReport.value = res.data.report;
    } catch (e) {
        if (retries.value < 3) {
            retries.value++

            setTimeout(function() {
                loadInsightsReport()
            }, 30000)
        } else {
            insightsError.value = true;
        }
    } finally {
        if (insightsAvailable.value || budgetExceeded.value || retries.value >= 3) {
            insightsLoading.value = false;
        }
    }
};

onMounted(() => {
    loadInsightsReport();
});
</script>

<template>
    <slideout-with-slots
        max-width-class="max-w-5xl"
        :resizeable="true"
        @close="emit('close')"
    >
        <template #title>
            <div>
                <h1 class="text-lg font-semibold">Closer</h1>
                <p v-if="leadName || leadCompany" class="text-sm opacity-80">
                    {{ leadName }}<span v-if="leadName && leadCompany"> &middot; </span>{{ leadCompany }}
                </p>
            </div>
        </template>

        <template #body>
            <!-- Tab bar -->
            <div class="flex border-b border-gray-200 mb-4 -mt-2">
                <button
                    class="px-4 py-2.5 text-sm font-medium border-b-2 transition-colors"
                    :class="activeTab === 'report'
                        ? 'border-aiAgentSalesAssistant text-gray-900'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'"
                    @click="activeTab = 'report'"
                >
                    <mf-translate domainkey="crm:nina.tab.report"></mf-translate>
                </button>
                <button
                    v-if="insightsReport && insightsReport.data"
                    class="px-4 py-2.5 text-sm font-medium border-b-2 transition-colors"
                    :class="activeTab === 'chat'
                        ? 'border-aiAgentSalesAssistant text-gray-900'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'"
                    @click="activeTab = 'chat'"
                >
                    <mf-translate domainkey="crm:nina.tab.askNina"></mf-translate>
                </button>
            </div>

            <!-- Report tab -->
            <div v-show="activeTab === 'report'">
                <div v-if="insightsLoading" class="flex items-center justify-center py-12">
                    <div>
                        <mf-spinner-medium class="w-24 h-12" />
                        <p class="text-center text-gray-400">Loading report, please wait...</p>
                    </div>
                </div>

                <closer-budget-exceeded v-else-if="budgetExceeded" />

                <div v-else-if="insightsError || !domain" class="text-center py-12">
                    <div class="flex items-center justify-center w-14 h-14 mx-auto mb-4 rounded-full bg-gray-100">
                        <svg class="w-7 h-7 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                            <circle cx="12" cy="12" r="10" />
                            <path d="M12 16v-4" />
                            <path d="M12 8h.01" />
                        </svg>
                    </div>
                    <p class="text-sm text-gray-500">
                        <mf-translate domainkey="crm:nina.noInsights"></mf-translate>
                    </p>
                </div>

                <div v-else-if="insightsReport && insightsReport.data">
                    <report-view
                        :domain="domain"
                        :report="insightsReport.data"
                        :updated-at="insightsReport.updated_at"
                        section-class="pb-8 border-b"
                        section-container="div"
                    />
                </div>
            </div>

            <!-- Chat tab -->
            <div v-show="activeTab === 'chat'">
                <nina-chat
                    :lead-id="leadId"
                    :domain="domain"
                    :insights-loaded="insightsReport !== null"
                />
            </div>
        </template>
    </slideout-with-slots>
</template>
